import React, { useState, useEffect } from 'react';
import { 
  HeartIcon, 
  BookOpenIcon, 
  ShoppingBagIcon, 
  DocumentTextIcon,
  SparklesIcon,
  TagIcon,
  GlobeAltIcon,
  EnvelopeIcon,
  ComputerDesktopIcon,
  UserGroupIcon,
  BriefcaseIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { getFavoriteResourcesWithDetails, getFavoriteDiscountsWithDetails } from '../lib/db';
import { Link, useNavigate } from 'react-router-dom';
import FavoriteButton from '../components/common/FavoriteButton';
import Tooltip from '../components/Tooltip';

const Collection = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [favoriteResources, setFavoriteResources] = useState([]);
  const [favoriteDiscounts, setFavoriteDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // Fetch favorites on mount
  useEffect(() => {
    const fetchFavorites = async () => {
      if (!user?.id) return;

      setLoading(true);
      try {
        const [resourcesResult, discountsResult] = await Promise.all([
          getFavoriteResourcesWithDetails(user.id),
          getFavoriteDiscountsWithDetails(user.id)
        ]);

        if (resourcesResult.success) {
          setFavoriteResources(resourcesResult.data);
        }

        if (discountsResult.success) {
          setFavoriteDiscounts(discountsResult.data);
        }
      } catch (error) {
        console.error('Error fetching favorites:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFavorites();
  }, [user?.id]);

  // Handle favorite removal from the page
  const handleFavoriteToggle = (isFavorited, itemId, itemType) => {
    if (!isFavorited) {
      // Item was removed from favorites
      if (itemType === 'resource') {
        setFavoriteResources(prev => prev.filter(item => item.id !== itemId));
      } else if (itemType === 'discount') {
        setFavoriteDiscounts(prev => prev.filter(item => item.id !== itemId));
      }
    }
  };

  // Helper to get resource type icon
  const getResourceTypeIcon = (type) => {
    switch ((type || '').toLowerCase()) {
      case 'playbook':
        return BookOpenIcon;
      case 'guide':
        return DocumentTextIcon;
      default:
        return BookOpenIcon;
    }
  };

  // Sort and filter functions
  const sortItems = (items, type) => {
    const sortedItems = [...items];
    
    switch (sortBy) {
      case 'newest':
        return sortedItems.sort((a, b) => new Date(b.favorited_at) - new Date(a.favorited_at));
      case 'oldest':
        return sortedItems.sort((a, b) => new Date(a.favorited_at) - new Date(b.favorited_at));
      case 'alphabetical':
        if (type === 'resource') {
          return sortedItems.sort((a, b) => a.title.localeCompare(b.title));
        } else {
          return sortedItems.sort((a, b) => a.name.localeCompare(b.name));
        }
      default:
        return sortedItems;
    }
  };

  const filteredResources = sortItems(favoriteResources, 'resource');
  const filteredDiscounts = sortItems(favoriteDiscounts, 'discount');

  const totalFavorites = favoriteResources.length + favoriteDiscounts.length;

  if (loading) {
    return (
      <div className="w-full mx-auto max-w-7xl">
        <div className="flex items-center gap-3 mb-8">
          <HeartIcon className="h-8 w-8 text-primary" />
          <h1 className="text-2xl font-bold text-white">My Collection</h1>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-gray-900 rounded-lg overflow-hidden shadow-lg animate-pulse h-80">
              <div className="w-full h-40 bg-gray-800" />
              <div className="p-5">
                <div className="h-4 bg-gray-800 w-1/4 mb-2 rounded" />
                <div className="h-6 bg-gray-800 w-3/4 mb-2 rounded" />
                <div className="h-4 bg-gray-800 w-full mb-4 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full mx-auto max-w-7xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
        <div className="flex items-center gap-3">
          <HeartIcon className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-white">My Collection</h1>
            <p className="text-gray-400 text-sm">
              {totalFavorites} {totalFavorites === 1 ? 'item' : 'items'} saved
            </p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex gap-3">
          {/* Tab filter */}
          <div className="flex bg-gray-800 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'all' 
                  ? 'bg-primary text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              All ({totalFavorites})
            </button>
            <button
              onClick={() => setActiveTab('resources')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'resources' 
                  ? 'bg-primary text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Content ({favoriteResources.length})
            </button>
            <button
              onClick={() => setActiveTab('discounts')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'discounts' 
                  ? 'bg-primary text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
            Offers ({favoriteDiscounts.length})
            </button>
          </div>

          {/* Sort dropdown */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-primary"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="alphabetical">Alphabetical</option>
          </select>
        </div>
      </div>

      {/* Empty state */}
      {totalFavorites === 0 && (
        <div className="text-center py-16 bg-gray-900 rounded-lg">
          <HeartIcon className="mx-auto h-16 w-16 text-gray-600 mb-4" />
          <h3 className="text-xl font-medium text-white mb-2">No favorites yet</h3>
          <p className="text-gray-400 mb-6">
            Start building your collection by hearting content and membership offers you love
          </p>
          <div className="flex gap-4 justify-center">
            <Link
              to="/"
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            >
              Browse Content Library
            </Link>
            <Link
              to="/marketplace"
              className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Browse Marketplace
            </Link>
          </div>
        </div>
      )}

      {/* Content Grid */}
      {totalFavorites > 0 && (
        <div className="space-y-8">
          {/* Resources Section */}
          {(activeTab === 'all' || activeTab === 'resources') && filteredResources.length > 0 && (
            <section>
              {activeTab === 'all' && (
                <div className="flex items-center gap-3 mb-6">
                  <BookOpenIcon className="h-6 w-6 text-primary-light" />
                  <h2 className="text-xl font-semibold text-white">
                    Content ({favoriteResources.length})
                  </h2>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredResources.map(resource => (
                  resource.slug ? (
                    <Link
                      key={resource.id}
                      to={`/playbooks/${resource.slug.trim()}/intro`}
                      className="bg-gray-900 rounded-lg overflow-hidden shadow-lg transition-transform duration-200 hover:-translate-y-1 ring-1 ring-primary/20 hover:ring-primary/40 flex flex-col justify-between group h-full min-h-[380px] relative"
                    >
                      <div className="absolute top-3 right-3 z-10">
                        <FavoriteButton
                          itemId={resource.id}
                          itemType="resource"
                          size="md"
                          onToggle={handleFavoriteToggle}
                        />
                      </div>
                      <img src={resource.image} alt={resource.title} className="w-full h-40 object-cover rounded-t-lg" />
                      <div className="p-5 flex flex-col flex-1 justify-between gap-2">
                        <div className="flex items-center mb-2">
                          {(() => {
                            const Icon = getResourceTypeIcon(resource.category);
                            return <Icon className="h-5 w-5 text-primary-light mr-2" />;
                          })()}
                          <span className="text-xs font-semibold text-primary-light capitalize">
                            {resource.category || 'How To Guide'}
                          </span>
                        </div>
                        <div className="h-[72px] flex items-start mb-3">
                          <h3 className="text-base sm:text-lg font-semibold text-white line-clamp-3">
                            {resource.title}
                          </h3>
                        </div>
                        <Tooltip content={resource.description}>
                          <p className="text-sm text-gray-400 mb-4 line-clamp-3">{resource.description}</p>
                        </Tooltip>
                        <div className="flex-1" />
                        <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-800">
                          <span>{resource.author}</span>
                          <div className="flex items-center text-gray-400">
                            <CalendarIcon className="h-3 w-3 mr-1" />
                            <span>
                              {new Date(resource.favorited_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric'
                              })}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ) : null
                ))}
              </div>
            </section>
          )}

          {/* Discounts Section */}
          {(activeTab === 'all' || activeTab === 'discounts') && filteredDiscounts.length > 0 && (
            <section>
              {activeTab === 'all' && (
                <div className="flex items-center gap-3 mb-6">
                  <ShoppingBagIcon className="h-6 w-6 text-primary-light" />
                  <h2 className="text-xl font-semibold text-white">
                    Marketplace Offers ({favoriteDiscounts.length})
                  </h2>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredDiscounts.map(discount => (
                  <div 
                    key={discount.id}
                    className="bg-gray-900 rounded-lg overflow-hidden shadow-lg flex flex-col h-full cursor-pointer hover:shadow-xl transition-shadow relative"
                    onClick={() => navigate(`/marketplace/${discount.id}`)}
                  >
                    <div className="absolute top-3 right-3 z-10">
                      <FavoriteButton
                        itemId={discount.id}
                        itemType="discount"
                        size="md"
                        onToggle={handleFavoriteToggle}
                      />
                    </div>
                    <div className="relative">
                      <img
                        src={discount.image_url}
                        alt={discount.name}
                        className="w-full h-40 object-cover"
                      />
                    </div>
                    <div className="p-5 flex-1 flex flex-col">
                      <div className="flex items-center gap-2 mb-2">
                        {discount.category && Array.isArray(discount.category) && discount.category.length > 0 ? (
                          <span className="text-xs font-medium bg-gray-800 text-gray-300 px-2.5 py-1 rounded-full border border-gray-700 flex items-center gap-1.5">
                            {discount.category[0] === 'HR Tech' ? (
                              <ComputerDesktopIcon className="h-3.5 w-3.5" />
                            ) : discount.category[0] === 'HR Services' ? (
                              <BriefcaseIcon className="h-3.5 w-3.5" />
                            ) : (
                              <UserGroupIcon className="h-3.5 w-3.5" />
                            )}
                            {discount.category[0]}
                          </span>
                        ) : (
                          <span className="text-xs font-medium bg-gray-800/50 text-gray-400 px-2.5 py-1 rounded-full border border-gray-700 flex items-center gap-1.5">
                            <UserGroupIcon className="h-3.5 w-3.5" />
                            Uncategorized
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-2 line-clamp-2">
                        {discount.offer || 'Membership Offer'}
                      </h3>
                      <p className="text-sm text-gray-400 mb-4 line-clamp-3">{discount.description}</p>
                      
                      <div className="flex justify-between items-center text-xs text-gray-500 mb-4">
                        <div className="flex items-center">
                          {discount.discount_icon_url && (
                            <img 
                              src={discount.discount_icon_url} 
                              alt={`${discount.name} logo`} 
                              className="h-6 w-6 mr-2 object-contain rounded-full bg-gray-800 p-0.5 border border-gray-700" 
                            />
                          )}
                          <span className="text-gray-300">{discount.name}</span>
                        </div>
                        <div className="flex items-center text-gray-400">
                          <CalendarIcon className="h-3 w-3 mr-1" />
                          <span>
                            {new Date(discount.favorited_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                      </div>
                      
                      {/* Tags section */}
                      {discount.tags && discount.tags.length > 0 && (
                        <div className="mt-auto mb-3 flex flex-wrap gap-1.5">
                          {discount.tags.slice(0, 3).map((tag, index) => (
                            <span 
                              key={index}
                              className="text-xs font-medium bg-gray-800/50 text-gray-400 px-2 py-0.5 rounded-full border border-gray-700"
                            >
                              #{tag}
                            </span>
                          ))}
                          {discount.tags.length > 3 && (
                            <span className="text-xs font-medium bg-gray-800/50 text-gray-400 px-2 py-0.5 rounded-full border border-gray-700">
                              +{discount.tags.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                      
                      {/* Contact and website links */}
                      <div className="flex flex-wrap gap-2">
                        {discount.website_url && (
                          <a 
                            href={discount.website_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center text-xs text-primary hover:text-primary-light transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <GlobeAltIcon className="h-3.5 w-3.5 mr-1" />
                            Visit Website
                          </a>
                        )}
                        {discount.contact_email && (
                          <a 
                            href={`mailto:${discount.contact_email}`}
                            className="flex items-center text-xs text-primary hover:text-primary-light transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <EnvelopeIcon className="h-3.5 w-3.5 mr-1" />
                            Contact
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
};

export default Collection; 